/**
 * FormulaBuilder.js
 *
 * Modal for creating computed columns in the Data Explorer.
 * Supports two modes:
 *   1. Free-form expression  — let users write the formula manually and select from available fields
 *   2. Preset formula        — rolling_avg / delta / ratio (dropdown driven)
 */

import React, { useState, useEffect } from 'react';
import {
  Modal, Form, Input, Select, Typography, Space, Tag, Alert, InputNumber, Tooltip,
} from 'antd';
import {
  FunctionOutlined,
} from '@ant-design/icons';
import { validateFormula } from '../../utils/formulaEngine';
import { FIELD_LOOKUP } from './DataSourceSelector';

const { Option } = Select;
const { TextArea } = Input;

/**
 * @param {{ visible, onCancel, onSave, selectedFieldKeys, existingFormulaNames }} props
 *   selectedFieldKeys: string[] — flat list of currently selected field keys
 *   existingFormulaNames: string[] — names already in use (to prevent duplicates)
 */
export default function FormulaBuilder({
  visible, onCancel, onSave, selectedFieldKeys = [], existingFormulaNames = [],
}) {
  const [form] = Form.useForm();
  const [mode, setMode] = useState('expression');
  const [formulaText, setFormulaText] = useState('');
  const [validationError, setValidationError] = useState(null);

  // Handle text changes
  const handleFormulaChange = (e) => {
    setFormulaText(e.target.value);
    setValidationError(null);
  };

  // Reset on modal open/close
  useEffect(() => {
    if (visible) {
      form.resetFields();
      setMode('expression');
      setFormulaText('');
      setValidationError(null);
    }
  }, [visible, form]);

  // Handle save
  const handleSave = () => {
    form.validateFields().then(values => {
      const name = values.name.trim();

      if (existingFormulaNames.includes(name)) {
        setValidationError(`Column "${name}" already exists.`);
        return;
      }

      if (mode === 'expression') {
        // Validate expression
        const result = validateFormula(formulaText, selectedFieldKeys);
        if (!result.valid) {
          setValidationError(result.error);
          return;
        }
        onSave({
          id: Date.now().toString(),
          name,
          type: 'expression',
          expression: formulaText,
        });
      } else {
        // Preset formula
        const { presetType, baseCol, param, windowSize } = values;
        if (!baseCol) {
          setValidationError('Please select a base column.');
          return;
        }
        onSave({
          id: Date.now().toString(),
          name,
          type: presetType,
          baseCol,
          param,
          windowSize: windowSize || 7,
        });
      }
    });
  };

  return (
    <Modal
      title={
        <Space>
          <FunctionOutlined style={{ color: '#8E44AD' }} />
          <span>Add Computed Column</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      onOk={handleSave}
      okText="Add Column"
      width={560}
      destroyOnClose
    >
      <Form form={form} layout="vertical" size="middle">
        {/* Column name */}
        <Form.Item
          name="name"
          label="Column Name"
          rules={[{ required: true, message: 'Enter a name for the computed column' }]}
        >
          <Input placeholder="e.g. profit_pct" />
        </Form.Item>

        {/* ─── Expression mode ─── */}
        {mode === 'expression' && (
          <div style={{ position: 'relative' }}>
            <Form.Item label="Formula Expression">
              <TextArea
                value={formulaText}
                onChange={handleFormulaChange}
                placeholder="e.g. (net_revenue_post_dsm / gross_revenue) * 100"
                autoSize={{ minRows: 3, maxRows: 6 }}
                style={{ fontFamily: 'monospace', fontSize: 13 }}
              />
            </Form.Item>

            <div style={{ marginBottom: 12 }}>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                Available fields:
              </Typography.Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                {selectedFieldKeys.map(key => {
                  const meta = FIELD_LOOKUP[key] || {};
                  return (
                    <Tooltip key={key} title={key}>
                      <Tag
                        color={meta.color || 'blue'}
                        style={{ cursor: 'pointer', fontSize: 11, margin: 0 }}
                        onClick={() => {
                          setFormulaText(prev => prev + key);
                        }}
                      >
                        {meta.label || key}
                      </Tag>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ─── Preset mode ─── */}
        {mode === 'preset' && (
          <>
            <Form.Item name="presetType" label="Operation" rules={[{ required: true }]} initialValue="rolling_avg">
              <Select>
                <Option value="rolling_avg">Rolling Average</Option>
                <Option value="delta">Delta (Current − Previous)</Option>
                <Option value="ratio">Ratio (Col A / Col B)</Option>
              </Select>
            </Form.Item>

            <Form.Item name="baseCol" label="Base Column" rules={[{ required: true }]}>
              <Select placeholder="Select a field" showSearch>
                {selectedFieldKeys.map(k => (
                  <Option key={k} value={k}>{FIELD_LOOKUP[k]?.label || k}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.presetType !== curr.presetType}>
              {({ getFieldValue }) => {
                const type = getFieldValue('presetType');
                if (type === 'ratio') {
                  return (
                    <Form.Item name="param" label="Denominator Column" rules={[{ required: true }]}>
                      <Select placeholder="Select denominator" showSearch>
                        {selectedFieldKeys.map(k => (
                          <Option key={k} value={k}>{FIELD_LOOKUP[k]?.label || k}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  );
                }
                if (type === 'rolling_avg') {
                  return (
                    <Form.Item name="windowSize" label="Window Size (periods)" initialValue={7}>
                      <InputNumber min={2} max={100} style={{ width: '100%' }} />
                    </Form.Item>
                  );
                }
                return null;
              }}
            </Form.Item>
          </>
        )}
      </Form>

      {/* Validation error */}
      {validationError && (
        <Alert
          type="error"
          message={validationError}
          showIcon
          closable
          onClose={() => setValidationError(null)}
          style={{ marginTop: 8 }}
        />
      )}
    </Modal>
  );
}
